import { useFormContext } from 'react-hook-form';
import { motion } from 'framer-motion';
import { DollarSign, User, CreditCard, Receipt, Calendar, Mail, Hash, Key } from 'lucide-react';
import { StripeNodeFormValues, StripeOperation } from '../../../types/stripe';
import { STRIPE_OPERATION_INFO } from '../../../data/stripe-constants';
import { VariablePicker } from '../../VariablePicker';

interface Step3InputsProps {
	operation: StripeOperation;
	config: StripeNodeFormValues;
	onChange: (config: Partial<StripeNodeFormValues>) => void;
	nodeId: string;
}

export function Step3Inputs({ operation, config, onChange, nodeId }: Step3InputsProps) {
	const { register, watch, setValue } = useFormContext<StripeNodeFormValues>();
	const watchedOperation = watch('operation');

	const handleInputChange = (field: keyof StripeNodeFormValues, value: any) => {
		onChange({ [field]: value });
	};

	const renderPaymentIntentInputs = () => (
		<div className="space-y-4">
			<div>
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					Payment Intent Configuration
				</h4>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Amount Path <span className="text-red-500">*</span>
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.amountPath || ''}
							onChange={(value: string) => handleInputChange('amountPath', value)}
							placeholder="$.payment.amount or use {{amount}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the payment amount in your workflow context
						</p>
					</label>

					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Currency
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.currency || 'usd'}
							onChange={(value: string) => handleInputChange('currency', value)}
							placeholder="usd or use {{currency}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							Three-letter currency code (e.g., usd, eur, gbp)
						</p>
					</label>

					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Customer ID Path
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.customerIdPath || ''}
							onChange={(value: string) => handleInputChange('customerIdPath', value)}
							placeholder="$.customer.id or use {{customer_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the customer ID in your workflow context
						</p>
					</label>

					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Payment Method ID Path
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.paymentMethodIdPath || ''}
							onChange={(value: string) => handleInputChange('paymentMethodIdPath', value)}
							placeholder="$.payment.methodId or use {{payment_method_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the payment method ID in your workflow context
						</p>
					</label>
				</div>

				<div className="mt-4 space-y-3">
					<label className="flex items-center space-x-3">
						<input
							type="checkbox"
							{...register('confirm')}
							className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
						/>
						<span className="text-sm font-medium text-gray-900 dark:text-white">
							Confirm Payment Immediately
						</span>
					</label>

					<label className="flex items-center space-x-3">
						<input
							type="checkbox"
							{...register('captureMethod')}
							value="manual"
							className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
						/>
						<span className="text-sm font-medium text-gray-900 dark:text-white">
							Manual Capture (authorize only)
						</span>
					</label>
				</div>
			</div>
		</div>
	);

	const renderConfirmPaymentIntentInputs = () => (
		<div className="space-y-4">
			<div>
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					Confirm Payment Intent Configuration
				</h4>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Payment Intent ID Path <span className="text-red-500">*</span>
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.paymentIntentIdPath || ''}
							onChange={(value: string) => handleInputChange('paymentIntentIdPath', value)}
							placeholder="$.payment.intentId or use {{payment_intent_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the payment intent ID in your workflow context
						</p>
					</label>

					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Payment Method ID Path
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.paymentMethodIdPath || ''}
							onChange={(value: string) => handleInputChange('paymentMethodIdPath', value)}
							placeholder="$.payment.methodId or use {{payment_method_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the payment method ID in your workflow context
						</p>
					</label>
				</div>

				<div className="mt-4 space-y-3">
					<label className="flex items-center space-x-3">
						<input
							type="checkbox"
							{...register('captureMethod')}
							value="manual"
							className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
						/>
						<span className="text-sm font-medium text-gray-900 dark:text-white">
							Manual Capture (authorize only)
						</span>
					</label>
				</div>
			</div>
		</div>
	);

	const renderCustomerInputs = () => (
		<div className="space-y-4">
			<div>
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					Customer Configuration
				</h4>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Email Path <span className="text-red-500">*</span>
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.customerEmailPath || ''}
							onChange={(value: string) => handleInputChange('customerEmailPath', value)}
							placeholder="$.customer.email or use {{customer_email}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the customer email in your workflow context
						</p>
					</label>

					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Name Path
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.customerNamePath || ''}
							onChange={(value: string) => handleInputChange('customerNamePath', value)}
							placeholder="$.customer.name or use {{customer_name}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the customer name in your workflow context
						</p>
					</label>
				</div>
			</div>
		</div>
	);

	const renderSubscriptionInputs = () => (
		<div className="space-y-4">
			<div>
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					Subscription Configuration
				</h4>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Customer ID Path <span className="text-red-500">*</span>
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.subCustomerIdPath || ''}
							onChange={(value: string) => handleInputChange('subCustomerIdPath', value)}
							placeholder="$.customer.id or use {{customer_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the customer ID in your workflow context
						</p>
					</label>

					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Price ID Path <span className="text-red-500">*</span>
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.priceIdPath || ''}
							onChange={(value: string) => handleInputChange('priceIdPath', value)}
							placeholder="$.subscription.priceId or use {{price_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the Stripe price ID in your workflow context
						</p>
					</label>

					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Trial Days Path
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.trialDaysPath || ''}
							onChange={(value: string) => handleInputChange('trialDaysPath', value)}
							placeholder="$.subscription.trialDays or use {{trial_days}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the trial period days in your workflow context
						</p>
					</label>

					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Cancel at Period End
						</span>
						<input
							type="checkbox"
							{...register('cancelAtPeriodEnd')}
							className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							Cancel the subscription at the end of the current billing period
						</p>
					</label>
				</div>
			</div>
		</div>
	);

	const renderInvoiceInputs = () => (
		<div className="space-y-4">
			<div>
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					Invoice Configuration
				</h4>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Customer ID Path <span className="text-red-500">*</span>
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.invoiceCustomerIdPath || ''}
							onChange={(value: string) => handleInputChange('invoiceCustomerIdPath', value)}
							placeholder="$.customer.id or use {{customer_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the customer ID in your workflow context
						</p>
					</label>

					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Send To Email Path
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.sendToEmailPath || ''}
							onChange={(value: string) => handleInputChange('sendToEmailPath', value)}
							placeholder="$.customer.email or use {{customer_email}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the email address to send the invoice to
						</p>
					</label>
				</div>

				<div className="mt-4 space-y-3">
					<label className="flex items-center space-x-3">
						<input
							type="checkbox"
							{...register('invoiceAutoAdvance')}
							className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
						/>
						<span className="text-sm font-medium text-gray-900 dark:text-white">
							Auto-advance invoice
						</span>
					</label>
				</div>
			</div>
		</div>
	);

	const renderRefundInputs = () => (
		<div className="space-y-4">
			<div>
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					Refund Configuration
				</h4>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Payment Intent ID Path
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.paymentIntentIdPath || ''}
							onChange={(value: string) => handleInputChange('paymentIntentIdPath', value)}
							placeholder="$.payment.intentId or use {{payment_intent_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the payment intent ID in your workflow context
						</p>
					</label>

					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Charge ID Path
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.chargeIdPath || ''}
							onChange={(value: string) => handleInputChange('chargeIdPath', value)}
							placeholder="$.payment.chargeId or use {{charge_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the charge ID in your workflow context
						</p>
					</label>

					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Refund Amount Path
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.refundAmountPath || ''}
							onChange={(value: string) => handleInputChange('refundAmountPath', value)}
							placeholder="$.refund.amount or use {{refund_amount}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the refund amount in your workflow context (leave empty for full refund)
						</p>
					</label>
				</div>
			</div>
		</div>
	);

	const renderAttachPaymentMethodInputs = () => (
		<div className="space-y-4">
			<div>
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					Payment Method Attachment
				</h4>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Customer ID Path <span className="text-red-500">*</span>
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.attachCustomerIdPath || ''}
							onChange={(value: string) => handleInputChange('attachCustomerIdPath', value)}
							placeholder="$.customer.id or use {{customer_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the customer ID in your workflow context
						</p>
					</label>

					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Payment Method ID Path <span className="text-red-500">*</span>
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.attachPaymentMethodIdPath || ''}
							onChange={(value: string) => handleInputChange('attachPaymentMethodIdPath', value)}
							placeholder="$.payment.methodId or use {{payment_method_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the payment method ID in your workflow context
						</p>
					</label>
				</div>

				<div className="mt-4 space-y-3">
					<label className="flex items-center space-x-3">
						<input
							type="checkbox"
							{...register('setDefault')}
							className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
						/>
						<span className="text-sm font-medium text-gray-900 dark:text-white">
							Set as default payment method
						</span>
					</label>
				</div>
			</div>
		</div>
	);

	const renderCancelSubscriptionInputs = () => (
		<div className="space-y-4">
			<div>
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					Subscription Cancellation
				</h4>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Subscription ID Path <span className="text-red-500">*</span>
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.subscriptionIdPath || ''}
							onChange={(value: string) => handleInputChange('subscriptionIdPath', value)}
							placeholder="$.subscription.id or use {{subscription_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the subscription ID in your workflow context
						</p>
					</label>
				</div>

				<div className="mt-4 space-y-3">
					<label className="flex items-center space-x-3">
						<input
							type="checkbox"
							{...register('cancelAtPeriodEnd')}
							className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
						/>
						<span className="text-sm font-medium text-gray-900 dark:text-white">
							Cancel at period end (instead of immediately)
						</span>
					</label>
					<p className="text-xs text-gray-500 dark:text-gray-400 ml-7">
						If checked, the subscription will remain active until the end of the current billing period. If unchecked, it will be canceled immediately.
					</p>
				</div>
			</div>
		</div>
	);

	const renderFinalizeInvoiceInputs = () => (
		<div className="space-y-4">
			<div>
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					Invoice Finalization
				</h4>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Invoice ID Path <span className="text-red-500">*</span>
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.invoiceIdPath || ''}
							onChange={(value: string) => handleInputChange('invoiceIdPath', value)}
							placeholder="$.invoice.id or use {{invoice_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the invoice ID in your workflow context
						</p>
					</label>
				</div>
			</div>
		</div>
	);

	const renderSendInvoiceInputs = () => (
		<div className="space-y-4">
			<div>
				<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
					Invoice Sending
				</h4>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Invoice ID Path <span className="text-red-500">*</span>
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.invoiceIdPath || ''}
							onChange={(value: string) => handleInputChange('invoiceIdPath', value)}
							placeholder="$.invoice.id or use {{invoice_id}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the invoice ID in your workflow context
						</p>
					</label>

					<label className="block">
						<span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Send To Email Path
						</span>
						<VariablePicker
							nodeId={nodeId}
							value={config.sendToEmailPath || ''}
							onChange={(value: string) => handleInputChange('sendToEmailPath', value)}
							placeholder="$.customer.email or use {{customer_email}}"
							className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							JSONPath to the email address to send the invoice to
						</p>
					</label>
				</div>
			</div>
		</div>
	);

	const renderOperationSpecificInputs = () => {
		switch (watchedOperation) {
			case 'create_payment_intent':
				return renderPaymentIntentInputs();
			case 'confirm_payment_intent':
				return renderConfirmPaymentIntentInputs();
			case 'create_customer':
				return renderCustomerInputs();
			case 'create_subscription':
				return renderSubscriptionInputs();
			case 'cancel_subscription':
				return renderCancelSubscriptionInputs();
			case 'create_invoice':
				return renderInvoiceInputs();
			case 'finalize_invoice':
				return renderFinalizeInvoiceInputs();
			case 'send_invoice':
				return renderSendInvoiceInputs();
			case 'refund_payment':
				return renderRefundInputs();
			case 'attach_payment_method':
				return renderAttachPaymentMethodInputs();
			default:
				return (
					<div className="p-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg">
						<p className="text-sm text-gray-600 dark:text-gray-400">
							Select an operation to configure its specific inputs.
						</p>
					</div>
				);
		}
	};

	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
					Operation Inputs
				</h3>
				<p className="text-sm text-gray-600 dark:text-gray-400">
					Configure the inputs for the selected Stripe operation
				</p>
			</div>

			{renderOperationSpecificInputs()}

			{/* Metadata Configuration */}
			<div className="space-y-4">
				<div>
					<h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
						Metadata Configuration
					</h4>
					<div className="p-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg">
						<p className="text-sm text-gray-600 dark:text-gray-400">
							Metadata will be automatically extracted from your workflow context using JSONPath patterns.
							You can configure specific metadata fields in the output mapping step.
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}
